import { AccountCircleOutlinedIcon, AppBar, Box, Button, Menu, MenuItem } from '@ds/coolshop';
import { AppBarNav } from '@modules/app-bar';
import { UI_APPBAR_ROLES } from '@src/_new/modules/auth/appBarRoles';
import { useCanAccessHierarchies } from '@src/_new/modules/auth/canAccessHierarchies';
import { CartNavButton } from '@src/_new/modules/cpq/components/Cart/CartNavButton';
import { GlobalSearchInput } from '@src/_new/modules/global-search/components/GlobalSearchInput';
import { NotificationBell } from '@src/_new/modules/notifications';
import { useAuth } from '@src/hooks';
import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { SettingsContext } from '../../modules/setting/SettingsContext';

/**
 * Cool PIM Header [Language |  Products | Hierarchies]
 */
export const PimAppBar = () => {
    const intl = useIntl();
    const { theme: themeSettings, globalSearch: globalSearchSettings } =
        useContext(SettingsContext);
    const { hasUserRole, sessionData } = useAuth();
    const canAccessHierarchies = useCanAccessHierarchies();
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const displayUserName =
        sessionData?.name || sessionData?.surname
            ? `${sessionData?.name} ${sessionData.surname}`
            : sessionData?.username;

    return (
        <AppBar position="fixed" color="transparent" divider>
            <Box display="flex" alignItems="center" flexShrink={0} marginRight={t => t.spacing(5)}>
                <img
                    style={{ height: '32px' }}
                    alt="CoolPim"
                    src={sessionData?.attributes?.logo ?? themeSettings?.logo ?? '/logoBlack.png'}
                />
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center" overflowX="auto">
                <AppBarNav />
            </Box>
            {displayUserName && (
                <Box
                    display="flex"
                    flexShrink={0}
                    alignItems="center"
                    marginLeft={t => t.spacing(2)}
                >
                    <Box>
                        {globalSearchSettings?.hierarchyIds?.length &&
                            hasUserRole(UI_APPBAR_ROLES.GLOBAL_SEARCH) &&
                            canAccessHierarchies && <GlobalSearchInput />}
                    </Box>
                    {/* The bell, then the cart, as in the app bar visuals. */}
                    <Box ml={2}>
                        {hasUserRole(UI_APPBAR_ROLES.NOTIFICATIONS) && <NotificationBell />}
                    </Box>
                    <Box ml={2}>{hasUserRole(UI_APPBAR_ROLES.CART) && <CartNavButton />}</Box>
                    <Box
                        ml={2}
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        // marginLeft="auto"
                    >
                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<AccountCircleOutlinedIcon />}
                            onClick={handleClick}
                        >
                            {displayUserName}
                        </Button>

                        <Menu
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    navigate('/profile');
                                }}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Profile',
                                    id: 'user-profile.title',
                                })}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    window.location.href = '/logout.html';
                                }}
                            >
                                {intl.formatMessage({ defaultMessage: 'Logout', id: 'logout' })}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            )}
        </AppBar>
    );
};

export default PimAppBar;
